import { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { FaArrowLeft } from 'react-icons/fa'
import { authService } from '../services'
import { useTranslation } from 'react-i18next'

export default function ForgotPassword() {
  const { t } = useTranslation()
  const [email, setEmail] = useState('')
  const [showRequired, setShowRequired] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)
  const navigate = useNavigate()

  const handleBack = () => {
    navigate('/login')
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    // Validate inputs
    if (!email) {
      setShowRequired(true)
      return
    }
    
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(email)) {
      setError(t('forgotPassword.errors.invalidEmail'))
      return
    }
    
    setLoading(true)
    setError('')
    
    try {
      // Use the authService to request a password reset
      const response = await authService.requestPasswordReset(email)
      
      if (response.success) {
        setSuccess(true)
      } else {
        setError(response.message || t('forgotPassword.errors.sendFailed'))
      }
    } catch (err: any) {
      console.error('Password reset error:', err)
      
      // Extract error message from the response if available
      let errorMessage = t('forgotPassword.errors.sendFailed')
      
      if (err.response) {
        const responseError = err.response.data?.error
        
        if (responseError) {
          errorMessage = responseError
        }
      } else if (err.message) {
        errorMessage = err.message
      }
      
      setError(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <div className="p-6 flex-1">
        {/* Navbar with circular back button */}
        <div className="flex items-center mb-8">
          <button onClick={handleBack} className="mr-4 p-2 bg-gray-200 rounded-full">
            <FaArrowLeft className="text-gray-700 text-xl font-bold" />
          </button>
        </div>

        <h2 className="text-2xl font-semibold mb-2">{t('forgotPassword.pageTitle')}</h2>

        <p className="text-gray-700 text-sm mb-6">
          {t('forgotPassword.instructions')}
        </p>

        {success ? (
          <div className="bg-green-50 p-4 rounded-[16px] mb-6">
            <p className="text-green-700 font-medium">{t('forgotPassword.success.emailSent')}</p>
            <p className="text-green-600 text-sm mt-1">
              {t('forgotPassword.success.checkInbox')}
            </p>
            <button 
              onClick={() => navigate('/login')}
              className="w-full h-[60px] rounded-[16px] font-medium text-black mt-6 bg-[#F9D348]"
            >
              {t('forgotPassword.buttons.returnToLogin')}
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="flex flex-col h-full">
            <input
              type="email"
              placeholder={t('forgotPassword.form.emailPlaceholder')}
              value={email}
              onChange={(e) => {
                setEmail(e.target.value)
                setShowRequired(false)
                setError('')
              }}
              className="w-full p-4 border border-gray-300 rounded-[16px] mb-4 focus:outline-none focus:border-gray-500"
            />
            
            {showRequired && (
              <span className="text-red-500 text-sm mb-4">
                {t('forgotPassword.errors.emailRequired')}
              </span>
            )}
            
            {error && (
              <span className="text-red-500 text-sm mb-4">{error}</span>
            )}

            <button 
              type="submit"
              disabled={loading}
              className={`w-full h-[60px] rounded-[16px] font-medium text-black mt-6 ${
                email ? 'bg-[#F9D348]' : 'bg-[#FDEDB6]'
              }`}
            >
              {loading ? t('forgotPassword.buttons.processing') : t('forgotPassword.buttons.sendInstructions')}
            </button>
          </form>
        )}

        <div className="mt-6 text-center">
          <p className="text-gray-600 text-sm">
            {t('forgotPassword.login.rememberPassword')}{' '}
            <Link to="/login" className="text-blue-600 font-medium">
              {t('forgotPassword.login.loginLink')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  )
} 